/**
 * Seller intake privacy inventory and sanitizers (M16) — client-side.
 * @see docs/seller-intake-privacy-consent-review.md
 */

export const SELLER_INTAKE_SENSITIVE_FIELDS = Object.freeze([
  "sellerName",
  "sellerPhone",
  "sellerEmail",
  "plate",
  "vin",
  "notes",
  "conditionSummary",
  "internalNotes",
  "communicationNotes",
  "lastContactSummary",
  "sellerAvailabilityWindow",
  "sellerResponseStatus",
]);

export const SELLER_INTAKE_ANALYTICS_FORBIDDEN_FIELDS = Object.freeze([
  ...SELLER_INTAKE_SENSITIVE_FIELDS,
  "submissionId",
  "uploadToken",
  "submissionToken",
  "duplicateHash",
  "honeypot",
  "rawPayload",
  "raw",
  "rawSource",
  "storageKey",
  "originalFileName",
  "imageUrl",
  "imageUrls",
  "fileName",
  "publicUrl",
  "thumbnailUrl",
  "condition.notes",
  "meta.honeypot",
  "seller.phone",
  "seller.email",
  "seller.name",
  "vehicle.plate",
  "vehicle.vin",
  "fieldErrors",
  "errors",
  "stack",
  "stackTrace",
  "internalMessage",
]);

export const SELLER_INTAKE_PUBLIC_FORBIDDEN_FIELDS = Object.freeze([
  ...SELLER_INTAKE_SENSITIVE_FIELDS,
  "submissionToken",
  "duplicateHash",
  "honeypot",
  "internalNotes",
  "communicationNotes",
  "storageKey",
  "sourceSubmissionId",
]);

/** @deprecated use SELLER_INTAKE_ANALYTICS_FORBIDDEN_FIELDS */
export const SELLER_ANALYTICS_BLOCKED_KEYS = SELLER_INTAKE_ANALYTICS_FORBIDDEN_FIELDS;

/** @deprecated use SELLER_INTAKE_SENSITIVE_FIELDS */
export const SELLER_PII_FIELDS = SELLER_INTAKE_SENSITIVE_FIELDS;

/** @deprecated */
export const SELLER_SENSITIVE_VEHICLE_FIELDS = Object.freeze(["plate", "vin"]);

/**
 * @param {string} fieldName
 */
export function isSellerIntakeSensitiveField(fieldName) {
  if (!fieldName) return false;
  const normalized = String(fieldName).trim();
  return (
    SELLER_INTAKE_SENSITIVE_FIELDS.includes(normalized) ||
    normalized.startsWith("seller.") ||
    normalized === "vehicle.plate" ||
    normalized === "vehicle.vin" ||
    SELLER_INTAKE_PUBLIC_FORBIDDEN_FIELDS.includes(normalized)
  );
}

/** @deprecated use isSellerIntakeSensitiveField */
export const isSensitiveSellerField = isSellerIntakeSensitiveField;

/**
 * @param {string|null|undefined} phone
 */
export function maskSellerPhone(phone) {
  if (phone == null || phone === "") return null;
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return "***";
  if (digits.length <= 4) return "***";
  return `*** *** ${digits.slice(-4)}`;
}

/**
 * @param {string|null|undefined} email
 */
export function maskSellerEmail(email) {
  if (!email) return null;
  const text = String(email).trim();
  const at = text.indexOf("@");
  if (at <= 1) return "***@***";
  return `${text.charAt(0)}***${text.slice(at)}`;
}

/**
 * @param {string|null|undefined} name
 */
export function maskSellerName(name) {
  if (!name) return null;
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
}

/**
 * Client-side guard for accidental logging of seller payloads.
 * @param {object} payload
 */
export function sanitizeSellerIntakeLogPayload(payload = {}) {
  const clean = {};
  for (const [key, value] of Object.entries(payload)) {
    if (isSellerIntakeSensitiveField(key)) continue;
    if (["status", "errorCategory", "sourcePage", "sourceComponent", "sellerType", "event"].includes(key)) {
      if (value != null && typeof value !== "object") clean[key] = value;
    }
  }
  return clean;
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionResult|null|undefined} result
 */
export function sanitizeSellerSubmissionForPublicResponse(result) {
  if (!result) return null;
  return {
    ok: result.ok,
    status: result.status,
    sellerMessage: result.sellerMessage,
    retryable: result.retryable,
    submissionId: result.submissionId || null,
    uploadToken: result.uploadToken || null,
    photoUploadStatus: result.photoUploadStatus || null,
    fieldErrors: Array.isArray(result.fieldErrors) ? result.fieldErrors : [],
    errorCategory: result.errorCategory || null,
    submittedAt: result.submittedAt || null,
  };
}
